import React, { useState } from 'react';
import './GroupPricesTab.css';
import api from '../../wrapper/API';
import Popup from '../../popup/Popup';
import ActionButton from '../../input/ActionButton';
import { usePopup } from '../../context/PopupContext';
import { useSuccessBar } from '../../context/SuccessBarContext';
import { formatEuro, samePrice, toPriceInput } from '../../../util/eventPrices';
import { apiErrorMessage, formatDateDe, PRICE_MAX } from './groupAdminUtils';

const openParticipationsLabel = (n) => {
  if (!n) return 'keine offenen Teilnahmen';
  return n === 1 ? '1 offene Teilnahme' : `${n} offene Teilnahmen`;
};

const repricedSentence = (n) => {
  if (!n) return '';
  return n === 1
    ? '1 offene Teilnahme dieser Gruppe wird neu berechnet. '
    : `${n} offene Teilnahmen dieser Gruppe werden neu berechnet. `;
};

function PriceChange({ label, from, to }) {
  if (samePrice(from, to)) return null;
  return (
    <span className="groupPricesChange">
      {label} <span className="groupPricesChangeOld">{formatEuro(from)}</span>
      <span className="material-symbols-rounded groupPricesChangeArrow" aria-label="wird zu">arrow_forward</span>
      <b>{formatEuro(to)}</b>
    </span>
  );
}

/**
 * Tab "Preise": default entry price of the group (applies to new events) and "Auf zukünftige Spieltage
 * anwenden…" (preview → confirm → apply) for the saved value. Add-on prices (e.g. Leihausrüstung, also per
 * group) live in the add-on catalog (/prices) and per process.
 */
function GroupPricesTab({ form, setField, errors, group, isCreate, copySource, unsavedPrices, isOffline, showError }) {
  const { openPopup, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  // Create with settings source: the API takes the source prices, so show them read-only.
  const locked = isCreate && !!copySource;
  const entryValue = locked ? toPriceInput(copySource.defaultEntryPrice) : form.defaultEntryPrice;

  const handlePreview = () => {
    if (!group) return;
    setBusy(true);
    api.post(`/ParticipantGroup/${group.id}/PreviewDefaultPrices`)
      .then(res => {
        const data = res.data ?? { eventCount: 0, participantCount: 0, events: [] };
        setPreview(data);
        openPopup(Number(data.eventCount) > 0 ? 'confirmapplygroupprices' : 'groupprices-uptodate', group.id);
      })
      .catch(err => showError('Vorschau fehlgeschlagen', apiErrorMessage(err, 'Die betroffenen Spieltage konnten nicht ermittelt werden.')))
      .finally(() => setBusy(false));
  };

  const handleApply = () => {
    closePopup();
    if (!group) return;
    setBusy(true);
    startSuccessBarLoading('Preise werden übernommen');
    api.post(`/ParticipantGroup/${group.id}/ApplyDefaultPrices`)
      .then(res => {
        const n = Number(res.data?.eventCount ?? 0);
        startSuccessBarSuccess(n === 1 ? 'Preise für 1 Spieltag übernommen' : `Preise für ${n} Spieltage übernommen`);
      })
      .catch(err => {
        closeSuccessBar();
        showError('Übernehmen fehlgeschlagen', apiErrorMessage(err, 'Die Preise konnten nicht übernommen werden.'));
      })
      .finally(() => setBusy(false));
  };

  let applyHint;
  if (isOffline) applyHint = 'Offline nicht verfügbar.';
  else if (unsavedPrices) applyHint = 'Erst speichern – übernommen werden immer die gespeicherten Standardpreise.';
  else applyHint = 'Übernimmt den gespeicherten Standard-Eintritt in alle zukünftigen, nicht abgesagten Spieltage. '
    + 'Vorher siehst du, welche Spieltage sich ändern.';

  return (
    <div className="groupTabBody">
      <section className="groupEditorSection">
        <h4 className="groupEditorSectionTitle">Standardpreis</h4>
        <div className="formFieldRow">
          <div className="formFieldWrapper">
            <label className="formFieldLabel" htmlFor="groupEntryPrice">Eintritt (€)</label>
            <input
              id="groupEntryPrice"
              type="number" min={0} max={PRICE_MAX} step={0.01} inputMode="decimal"
              value={entryValue}
              onChange={e => setField('defaultEntryPrice', e.target.value)}
              disabled={locked}
              aria-invalid={errors.defaultEntryPrice ? 'true' : undefined}
              className={errors.defaultEntryPrice ? 'isInvalid' : ''}
            />
          </div>
        </div>
        {errors.defaultEntryPrice && <p className="groupEditorFieldError">{errors.defaultEntryPrice}</p>}
        <p className="formFieldHint">
          {locked
            ? `Wird von „${copySource.name}“ übernommen – nach dem Anlegen hier änderbar.`
            : '0 = kostenlos. Gilt für neu angelegte Spieltage; Prozessvorlagen können einen eigenen Eintritt festlegen. Bestehende Spieltage bleiben unverändert.'}
        </p>
        {group?.isGuest && !locked && (
          <p className="formFieldHint">Das ist der Eintritt der öffentlichen Anmeldung.</p>
        )}
        <p className="formFieldHint">
          Preise für Zusätze (z. B. Leihausrüstung) – auch eigene Preise dieser Gruppe – stehen auf der Preisliste und
          gelten je Prozess (Abschnitt „Zusätze“ der Prozessseite).
        </p>
      </section>

      {isCreate ? (
        <p className="formFieldHint">
          Beim Anlegen bekommen alle zukünftigen Spieltage eine Preiszeile für die neue Gruppe – mit diesem Eintritt
          bzw. dem Eintritt der jeweiligen Prozessvorlage.
        </p>
      ) : (
        <section className="groupEditorSection">
          <h4 className="groupEditorSectionTitle">Zukünftige Spieltage</h4>
          <div>
            <ActionButton
              icon="price_change"
              label={busy ? 'Wird geprüft …' : 'Auf zukünftige Spieltage anwenden …'}
              type={3}
              onClick={handlePreview}
              disabled={isOffline || unsavedPrices || busy}
            />
          </div>
          <p className={unsavedPrices && !isOffline ? 'groupEditorExplanation' : 'formFieldHint'}>{applyHint}</p>
        </section>
      )}

      {group && (
        <React.Fragment>
          <Popup
            name="confirmapplygroupprices"
            headerText="Preise übernehmen"
            confirmText="Übernehmen"
            type="warning"
            onConfirm={handleApply}
            withCancel
            wide
          >
            <p>
              Der Standard-Eintritt von <b>{group.name}</b> wird in{' '}
              {Number(preview?.eventCount) === 1 ? '1 zukünftigen Spieltag' : `${preview?.eventCount ?? 0} zukünftige Spieltage`} übernommen:
            </p>
            <ul className="groupPricesPreviewList">
              {(preview?.events ?? []).map(e => (
                <li key={e.eventId} className="groupPricesPreviewRow">
                  <span className="groupPricesPreviewHead">
                    <span className="groupPricesPreviewDate">{formatDateDe(e.date)}</span>
                    <span className="groupPricesPreviewAffected">{openParticipationsLabel(e.affectedParticipants)}</span>
                  </span>
                  <span className="groupPricesPreviewChanges">
                    <PriceChange label="Eintritt" from={e.oldEntryPrice} to={e.newEntryPrice} />
                  </span>
                </li>
              ))}
            </ul>
            <p className="formFieldHint">
              {repricedSentence(Number(preview?.participantCount ?? 0))}
              Bezahlte, manuell gesetzte und Spieltagleiter-Preise bleiben unverändert. Legt die Prozessvorlage eines
              Spieltags einen eigenen Eintritt fest, gilt dieser. Zusätze rechnen weiter mit den Preisen ihres Prozesses.
            </p>
          </Popup>
          <Popup name="groupprices-uptodate" headerText="Nichts zu übernehmen" confirmText="Ok">
            <p>Alle zukünftigen Spieltage verwenden bereits den Standard-Eintritt von <b>{group.name}</b>.</p>
          </Popup>
        </React.Fragment>
      )}
    </div>
  );
}

export default GroupPricesTab;
